import { useEffect, useState } from 'react'
import { ProfileLayout } from '@/components/layout/ProfileLayout'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { userSettingsApi } from '@/api/userSettings'
import { getErrorMessage } from '@/utils/error'
import { SettingsForm } from '@/components/settings/SettingsForm'
import type { UserSettings } from '@/types'
import '@/styles/dashboard.css'

export function SettingsPage() {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [voiceSaving, setVoiceSaving] = useState(false)

  useEffect(() => {
    userSettingsApi
      .getMySettings()
      .then(setSettings)
      .catch((err) => setError(getErrorMessage(err) || t('profile.settings.loadError')))
      .finally(() => setLoading(false))
  }, [t])

  const handleSubmit = async (data: Parameters<typeof userSettingsApi.updateMySettings>[0]) => {
    setError(null)
    setSuccessMsg(null)
    setSubmitLoading(true)
    try {
      const updated = await userSettingsApi.updateMySettings(data)
      setSettings(updated)
      setSuccessMsg(t('profile.settings.saveSuccess'))
    } catch (err) {
      setError(getErrorMessage(err) || t('profile.settings.saveError'))
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleVoiceGenderChange = async (_: unknown, value: 'male' | 'female' | null) => {
    if (!value || !settings) return
    setError(null)
    setVoiceSaving(true)
    try {
      const updated = await userSettingsApi.updateMySettings({ tts_voice_gender: value })
      setSettings(updated)
      setSuccessMsg(t('profile.settings.saveSuccess'))
    } catch (err) {
      setError(getErrorMessage(err) || t('profile.settings.saveError'))
    } finally {
      setVoiceSaving(false)
    }
  }

  return (
    <ProfileLayout>
      <Box sx={{ p: 3, pt: 0 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
          {t('profile.settings.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t('profile.settings.subtitle')}
        </Typography>

        {loading && (
          <Card>
            <CardContent>
              <Skeleton variant="text" width="40%" height={32} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="70%" sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMsg && (
          <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
            {successMsg}
          </Alert>
        )}

        {!loading && settings && (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('tts.voiceSettingTitle', 'Голос озвучивания слов')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t(
                    'tts.voiceSettingHelp',
                    'Этим голосом будут озвучиваться слова при генерации произношения.',
                  )}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={settings.tts_voice_gender ?? 'female'}
                  onChange={handleVoiceGenderChange}
                  disabled={voiceSaving}
                >
                  <ToggleButton value="female">
                    {t('tts.voiceFemale', 'Женский')}
                  </ToggleButton>
                  <ToggleButton value="male">
                    {t('tts.voiceMale', 'Мужской')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </CardContent>
            </Card>

            <SettingsForm
              initialData={settings}
              onSubmit={handleSubmit}
              isLoading={submitLoading}
            />
          </>
        )}
      </Box>
    </ProfileLayout>
  )
}
